import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import Toast from "@/components/base/Toast";
import { useToast } from "@/hooks/useToast";
import { heroPersons, heroStats } from "@/mocks/homeData";

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const { message, show } = useToast();

  const handleSearch = () => {
    if (!keyword.trim()) {
      show("先输入一个称呼或关键词吧");
      return;
    }
    show("单页演示模式，检索结果暂未接入");
  };

  return (
    <section id="hero" className="relative w-full overflow-hidden border-b border-background-200 bg-background-50">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-64 dotted-bg opacity-60" />
      <div className="relative mx-auto w-full max-w-[1180px] px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 font-label text-xs tracking-wide text-primary-700">
                <i className="ri-node-tree text-sm" />
                人物向 · 单页梳理
              </span>
              <h2 className="mt-5 font-heading text-3xl font-bold leading-snug tracking-tight text-foreground-950 md:text-[42px] md:leading-[1.25]">
                先把人认清，
                <br className="hidden sm:block" />
                再看当事人之间的关系脉络
              </h2>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
                海角黑料里出现的名字很多，但真正需要先弄清楚的，是主当事人是谁、有什么来历、和配角怎么连在一起。
                这一页不做结论，只把身份背景与人物关系按可核实的程度摊开，方便你一条条对照。
              </p>
            </div>

            <div className="w-full max-w-md lg:pb-2">
              <div className="flex items-center gap-2 rounded-lg border border-background-300 bg-background-50 p-1.5">
                <span className="flex h-8 w-8 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  placeholder="输入人物称呼或事件关键词"
                  className="min-w-0 flex-1 bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors duration-200 hover:bg-primary-600"
                >
                  检索
                </button>
              </div>
              <p className="mt-2 pl-1 font-label text-xs text-foreground-400">
                共收录 6 条人物记录 · 5 处关系节点
              </p>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-3 md:gap-5 lg:grid-cols-4">
          {heroPersons.map((person, index) => (
            <Reveal key={person.id} delay={index * 90}>
              <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-300 hover:border-foreground-300">
                <div className="relative h-[190px] w-full overflow-hidden md:h-[220px]">
                  <img
                    src={person.image}
                    alt={`海角黑料人物梳理 匿名示意图像 ${person.alias}`}
                    title={`海角黑料${person.alias}身份背景与关系脉络`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                  <span className="absolute left-3 top-3 rounded-full bg-foreground-950/80 px-2.5 py-1 font-label text-[10px] tracking-wide text-background-50 backdrop-blur-sm">
                    {person.tag}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-3.5 md:p-4">
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-lg font-bold text-foreground-950">{person.name}</span>
                    <span className="font-label text-xs text-accent-600">{person.alias}</span>
                  </div>
                  <p className="mt-2 flex-1 text-xs leading-relaxed text-foreground-600 md:text-[13px]">
                    {person.line}
                  </p>
                  <a
                    href="#archive"
                    className="mt-3 inline-flex items-center gap-1 font-label text-xs text-primary-700 transition-colors duration-200 hover:text-primary-600"
                  >
                    查看档案
                    <i className="ri-arrow-right-line text-sm" />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <dl className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-4">
            {heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1 bg-background-50 px-4 py-4 md:px-6 md:py-5">
                <dt className="font-label text-xs tracking-wide text-foreground-500">{stat.label}</dt>
                <dd className="flex items-baseline gap-1">
                  <span className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    {stat.value}
                  </span>
                  {stat.unit && <span className="font-label text-xs text-foreground-500">{stat.unit}</span>}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
      <Toast message={message} />
    </section>
  );
}